import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import Rating from './Rating';
import { ChecklistDisplay } from '../../Common/TaskChecklist';
import 'react-datepicker/dist/react-datepicker.css';
import './ClientArchives.css';
import '../CleanerDashboard/AvailableTasks.css';

// ── Mapping helpers ──
const SERVICE_TYPES = { home: 'Home Cleaning', commercial: 'Commercial Cleaning', moveinout: 'Move-in/Move-out' };
const CLEANING_CATEGORIES = { standard: 'Standard Cleaning', main: 'Main Cleaning', adhoc: 'Ad hoc Cleaning' };
const KNOWN_AS_NEEDED = ['wipingFrames', 'limeShower', 'vacuumFurniture', 'cobwebs'];
const KNOWN_MAIN_EXTRAS = [
  'wipingFrames', 'wipingWindowEdges', 'wipingCeilingEdges', 'windowCleaning',
  'blindsCleaned', 'furnitureVacuumed', 'behindFurniture', 'cleaningWallsCeilings',
  'underCarpets', 'mirrorsGlass', 'ceilingLights', 'closedSpaces',
  'insideCupboards', 'woodenFloors', 'ovenFridge',
];
const KNOWN_ADHOC = ['standardWithout', 'vacuumAndFloor', 'bathroomAndFloor', 'largeHome'];
const KNOWN_EXTRA_TARGETED = ['animalHair', 'smoking'];
const EQUIPMENT_PREFIX = 'equipment:';
const EQUIPMENT_KEYS = ['cleaningAgents', 'cloth', 'vacuumCleaner', 'mop', 'specialProducts'];
const ALL_KNOWN = new Set([...KNOWN_AS_NEEDED, ...KNOWN_MAIN_EXTRAS, ...KNOWN_ADHOC, ...KNOWN_EXTRA_TARGETED]);

const mapServiceType = (cleaningType) => {
  if (cleaningType === 'residential') return 'home';
  if (cleaningType === 'move') return 'moveinout';
  if (cleaningType === 'deep') return 'home';
  return cleaningType;
};
const mapCleaningCategory = (cleaningType) => (cleaningType === 'deep' ? 'main' : 'standard');

const ClientArchives = ({ userId }) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const getTaskIdNumber = (taskId) => {
    if (!taskId) return '';
    if (typeof taskId === 'number') return taskId.toString();
    const idStr = String(taskId);
    if (idStr.length === 24) return idStr.slice(-6);
    const match = idStr.match(/-(\d+)$/);
    return match ? match[1] : idStr;
  };

  const renderStars = (value) => {
    const rating = Math.max(0, Math.min(5, Number(value) || 0));
    const full = Math.round(rating);
    return `${'★'.repeat(full)}${'☆'.repeat(Math.max(0, 5 - full))}`;
  };

  useEffect(() => {
    fetchCompletedTasks();
  }, [userId, startDate, endDate]);

  const fetchCompletedTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      const response = await api.get('/tasks/completed', { params });
      let tasks = response.data.data || [];
      
      // Sort by date descending (latest first), then by createdAt
      tasks = tasks.sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setCompletedTasks(tasks);
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
      showToast(error.response?.data?.message || t('client.errorFetchingTasks', 'Error fetching completed tasks. Please try again.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSubmit = async (taskId, rating, comment) => {
    try {
      await api.post(`/tasks/${taskId}/rate`, { rating, ratingComment: comment });
      fetchCompletedTasks();
      if (selectedTask && (selectedTask._id || selectedTask.id) === taskId) {
        setSelectedTask(prev => prev ? { ...prev, rating, ratingComment: comment } : prev);
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast(error.response?.data?.message || t('client.errorRating', 'Error submitting rating. Please try again.'), 'error');
    }
  };

  if (loading) {
    return <div className="ca-loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="ca-container">
      <div className="ca-header">
        <div>
          <h2>{t('client.completedTasks', 'Completed Tasks')}</h2>
          <p className="ca-description">{t('client.completedDescription', 'Your completed cleaning jobs')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="ca-filters">
        <div className="ca-filter-group">
          <label>{t('common.from', 'From')}</label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText={t('common.startDate', 'Start date')}
            dateFormat="dd,MM,yyyy"
            className="ca-filter-input"
          />
        </div>
        <div className="ca-filter-group">
          <label>{t('common.to', 'To')}</label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText={t('common.endDate', 'End date')}
            dateFormat="dd,MM,yyyy"
            className="ca-filter-input"
          />
        </div>
        {(startDate || endDate) && (
          <button
            className="ca-btn ca-btn-secondary ca-btn-sm"
            onClick={() => { setStartDate(null); setEndDate(null); }}
          >
            {t('common.clearFilter', 'Clear Filter')}
          </button>
        )}
      </div>

      {completedTasks.length === 0 ? (
        <p className="ca-no-tasks">{t('client.noCompletedTasks', 'No completed tasks yet.')}</p>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="ca-table-wrapper">
            <table className="ca-table">
              <thead>
                <tr>
                  <th>{t('admin.taskId', 'Task ID')}</th>
                  <th>{t('auth.address', 'Address')}</th>
                  <th>{t('client.date', 'Date')}</th>
                  <th>{t('client.hours', 'Hours')}</th>
                  <th>{t('admin.type', 'Type')}</th>
                  <th>{t('client.cleaner', 'Cleaner')}</th>
                  <th>{t('client.yourRating', 'Rating')}</th>
                  <th>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {completedTasks.map(task => {
                  const taskId = task._id || task.id;
                  return (
                    <tr key={taskId}>
                      <td>#{getTaskIdNumber(taskId)}</td>
                      <td>{task.address}</td>
                      <td>{new Date(task.date).toLocaleDateString('en-GB')}</td>
                      <td>{task.hours}</td>
                      <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                      <td>{task.cleaner?.name || '—'}</td>
                      <td>{task.rating ? <span className="ca-rating-stars">{renderStars(task.rating)}</span> : '—'}</td>
                      <td>
                        <button className="ca-btn ca-btn-primary ca-btn-sm" onClick={() => setSelectedTask(task)}>
                          {t('common.details', 'Details')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="ca-cards">
            {completedTasks.map(task => {
              const taskId = task._id || task.id;
              return (
                <div key={taskId} className="ca-card">
                  <div className="ca-card-header">
                    <span className="ca-card-id">#{getTaskIdNumber(taskId)}</span>
                    <span className="ca-status ca-status-completed">{task.status}</span>
                  </div>
                  <div className="ca-card-details">
                    <div className="ca-card-row">
                      <span className="ca-card-label">{t('auth.address', 'Address')}:</span>
                      <span className="ca-card-value">{task.address}</span>
                    </div>
                    <div className="ca-card-row">
                      <span className="ca-card-label">{t('client.date', 'Date')}:</span>
                      <span className="ca-card-value">{new Date(task.date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="ca-card-row">
                      <span className="ca-card-label">{t('client.hours', 'Hours')}:</span>
                      <span className="ca-card-value">{task.hours}</span>
                    </div>
                    <div className="ca-card-row">
                      <span className="ca-card-label">{t('admin.serviceType', 'Service Type')}:</span>
                      <span className="ca-card-value">{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</span>
                    </div>
                    {task.cleaner && (
                      <div className="ca-card-row">
                        <span className="ca-card-label">{t('client.cleaner', 'Cleaner')}:</span>
                        <span className="ca-card-value">{task.cleaner.name}</span>
                      </div>
                    )}
                    {task.rating && (
                      <div className="ca-card-row">
                        <span className="ca-card-label">{t('client.yourRating', 'Rating')}:</span>
                        <span className="ca-card-value ca-rating-stars">{renderStars(task.rating)}</span>
                      </div>
                    )}
                  </div>
                  <div className="ca-card-actions">
                    <button className="ca-btn ca-btn-primary ca-btn-sm" onClick={() => setSelectedTask(task)}>
                      {t('common.details', 'Details')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Details Modal */}
      {selectedTask && (
        <div className="at-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="at-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal-header">
              <h3>{t('common.details', 'Details')} - #{getTaskIdNumber(selectedTask._id || selectedTask.id)}</h3>
              <button className="at-close-btn" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>
            <div className="at-modal-body">
              {/* Status */}
              <div className="at-detail-status">
                <span className="ca-status ca-status-completed">{selectedTask.status}</span>
              </div>

              {/* Service Type */}
              <div className="at-detail-section">
                <h4>{t('newOrder.serviceType', 'Service Type')}</h4>
                <p>{SERVICE_TYPES[mapServiceType(selectedTask.cleaningType)] || selectedTask.cleaningType}</p>
              </div>

              {/* Cleaner */}
              {selectedTask.cleaner && (
                <div className="at-detail-section">
                  <h4>{t('client.cleaner', 'Cleaner')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">👤</span>
                    <span>{selectedTask.cleaner.name}</span>
                  </div>
                </div>
              )}

              {/* Recurring Information */}
              {(selectedTask.isRecurring || selectedTask.recurrenceGroupId) && (
                <div className="at-detail-section">
                  <h4>{t('orders.recurringInfo', 'Recurring Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🔄</span>
                    <span>Every {selectedTask.recurrenceEvery ?? 1} week(s){selectedTask.recurrenceCount > 1 ? ` (${selectedTask.recurrenceCount} tasks)` : ''}</span>
                  </div>
                </div>
              )}

              {/* Title */}
              {selectedTask.title && (
                <div className="at-detail-section">
                  <h4>{t('orders.title', 'Title')}</h4>
                  <p>{selectedTask.title}</p>
                </div>
              )}

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('newOrder.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📍</span>
                  <span>{selectedTask.address || '—'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span>{new Date(selectedTask.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🕐</span>
                  <span>{selectedTask.allDay ? t('orders.allDay', 'All Day') : (selectedTask.time || '—')}{selectedTask.endTime ? ` – ${selectedTask.endTime}` : ''}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏳</span>
                  <span>{selectedTask.hours || '–'} {t('admin.hours', 'hours')}</span>
                </div>
              </div>

              {/* Dynamic checklist-derived sections */}
              {(() => {
                const checklist = Array.isArray(selectedTask.checklist) ? selectedTask.checklist : [];
                const asNeeded = checklist.filter(i => KNOWN_AS_NEEDED.includes(i));
                const mainExtras = checklist.filter(i => KNOWN_MAIN_EXTRAS.includes(i));
                const adhoc = checklist.filter(i => KNOWN_ADHOC.includes(i));
                const equipmentSelected = EQUIPMENT_KEYS.filter(k => checklist.includes(`${EQUIPMENT_PREFIX}${k}`));
                const extraTargeted = [];
                if (checklist.includes('animalHair')) extraTargeted.push(t('newOrder.animalHair', 'Animal hair'));
                if (checklist.includes('smoking')) extraTargeted.push(t('newOrder.smoking', 'Smoking'));
                const remaining = checklist.filter(item => typeof item === 'string' && !ALL_KNOWN.has(item) && !item.startsWith(EQUIPMENT_PREFIX));

                const equipmentLabels = {
                  cleaningAgents: t('newOrder.cleaningAgents', 'Cleaning agents'),
                  cloth: t('newOrder.cloth', 'Cloth'),
                  vacuumCleaner: t('newOrder.vacuumCleaner', 'Vacuum cleaner'),
                  mop: t('newOrder.mop', 'Mop'),
                  specialProducts: t('newOrder.specialProducts', 'Special products'),
                };

                const renderChecklistSection = (items, title) => items.length > 0 ? (
                  <div className="at-detail-section" key={title}>
                    <h4>{title}</h4>
                    <ul className="at-detail-list">
                      {items.map((item, i) => <li key={i}>✅ {item}</li>)}
                    </ul>
                  </div>
                ) : null;

                return (
                  <>
                    {renderChecklistSection(asNeeded, t('newOrder.asNeeded', 'As Needed'))}
                    {renderChecklistSection(mainExtras, t('newOrder.mainCleaningExtras', 'Main Cleaning Extras'))}
                    {renderChecklistSection(adhoc, t('newOrder.adhocOptions', 'Ad hoc Options'))}
                    {equipmentSelected.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('newOrder.equipment', 'Equipment')}</h4>
                        <ul className="at-detail-list">
                          {equipmentSelected.map((k) => <li key={k}>✅ {equipmentLabels[k] || k}</li>)}
                        </ul>
                      </div>
                    )}
                    {renderChecklistSection(extraTargeted, t('newOrder.extraTargeted', 'Extra Targeted'))}
                    {remaining.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('client.whatNeedsToBeDone', 'What needs to be done')}</h4>
                        <ChecklistDisplay items={remaining} completed={selectedTask.checklistCompleted} />
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('newOrder.additionalComments', 'Comments')}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {/* Rating */}
              {selectedTask.cleaner && (
                <div className="at-detail-section">
                  {selectedTask.rating ? (
                    <div className="ca-existing-rating">
                      <h4>⭐ {t('client.yourRating', 'Your Rating')}</h4>
                      <div className="ca-rating-display">
                        <span className="ca-rating-stars-lg">
                          {'★'.repeat(selectedTask.rating)}{'☆'.repeat(5 - selectedTask.rating)}
                        </span>
                        <span className="ca-rating-value">{selectedTask.rating} / 5</span>
                      </div>
                      {selectedTask.ratingComment && (
                        <p className="ca-rating-comment">{selectedTask.ratingComment}</p>
                      )}
                    </div>
                  ) : selectedTask.canRate ? (
                    <div className="ca-rating-prompt">
                      <h4>⭐ {t('client.rateService', 'Rate Your Cleaning Service')}</h4>
                      <p>{t('client.ratePrompt', 'How was your experience? Please rate and leave a comment to help us improve!')}</p>
                      <Rating
                        taskId={selectedTask._id || selectedTask.id}
                        cleanerName={selectedTask.cleaner.name}
                        onSubmit={handleRatingSubmit}
                      />
                    </div>
                  ) : selectedTask.ratingBlockedReason ? (
                    <div className="ca-rating-prompt">
                      <h4>⭐ {t('client.rateService', 'Rate Your Cleaning Service')}</h4>
                      <p>{selectedTask.ratingBlockedReason}</p>
                    </div>
                  ) : (
                    null
                  )}
                </div>
              )}

              {/* Order Date */}
              {selectedTask.createdAt && (
                <div className="at-detail-meta">
                  <span className="at-detail-meta-label">{t('orders.orderDate', 'Order Date')}</span>
                  <span>{new Date(selectedTask.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
              )}
            </div>
            <div className="at-modal-actions">
              <button className="ca-btn ca-btn-secondary" onClick={() => setSelectedTask(null)}>
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientArchives;
